import { ArrowLeft, Ban, CheckCircle2, Loader2, Pencil, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { API_CONFIG } from '../../config/api';
import { useAppState } from '../../context/AppStateContext';
import { Badge } from '../common/Badge';
import { ui } from '../../styles/ui';

const ROLE_TONE = {
  'Super Admin': 'danger',
  Admin: 'violet',
  'Field Officer': 'info',
  Dealer: 'success',
  User: 'neutral',
};

const ROLE_OPTIONS = ['Field Officer', 'Dealer', 'User'];

export const UserDetailsPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { users, loadUsers, apiCall, setUsers } = useAppState();
  const user = users.find((u) => u._id === userId);

  const [isEditing, setIsEditing] = useState(searchParams.get('edit') === '1');
  const [roleDraft, setRoleDraft] = useState(user?.role || '');
  const [saving, setSaving] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    if (!user) loadUsers(undefined, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // roleDraft needs the user to actually be loaded first (e.g. arriving via
  // a direct link with ?edit=1 before the users list has fetched).
  useEffect(() => {
    if (user && !roleDraft) setRoleDraft(user.role);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleBack = () => navigate('/admin/users');

  const startEdit = () => {
    setRoleDraft(user.role);
    setError('');
    setNotice('');
    setIsEditing(true);
  };

  const saveRole = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await apiCall(API_CONFIG.endpoints.changeRole(userId), 'PUT', { role: roleDraft }, true);
      setUsers((prev) => prev.map((u) => (u._id === userId ? { ...u, role: roleDraft } : u)));
      setIsEditing(false);
      setNotice(res.user?.password ? `Role changed. New admin password: ${res.user.password}` : 'Role updated successfully.');
    } catch (err) {
      setError(err.message || 'Failed to change role');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async () => {
    if (!window.confirm(`${user.isActive ? 'Deactivate' : 'Reactivate'} ${user.firstName} ${user.lastName}?`)) return;
    setStatusSaving(true);
    setError('');
    try {
      await apiCall(API_CONFIG.endpoints.toggleUserStatus(userId), 'PUT', null, true);
      setUsers((prev) => prev.map((u) => (u._id === userId ? { ...u, isActive: !u.isActive } : u)));
    } catch (err) {
      setError(err.message || 'Failed to update status');
    } finally {
      setStatusSaving(false);
    }
  };

  if (!user) {
    return (
      <div className="space-y-4 max-w-2xl">
        <button onClick={handleBack} className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
          <ArrowLeft className="h-4 w-4" />
          Back to Users
        </button>
        <div className={`${ui.card} p-8 text-center text-gray-500 dark:text-gray-400`}>
          <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
          Loading user...
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-2xl">
      <button onClick={handleBack} className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
        <ArrowLeft className="h-4 w-4" />
        Back to Users
      </button>

      <div className={`${ui.card} p-5`}>
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-violet-600 rounded-lg flex items-center justify-center shrink-0">
              <span className="text-white font-semibold text-sm">{user.firstName?.[0]}{user.lastName?.[0]}</span>
            </div>
            <div>
              <h1 className="text-base font-semibold text-gray-900 dark:text-white">{user.firstName} {user.lastName}</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">ID: {user._id?.slice(-8)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isEditing && (
              <button onClick={startEdit} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                <Pencil className="h-3.5 w-3.5" />
                Edit Role
              </button>
            )}
            <button
              onClick={toggleStatus}
              disabled={statusSaving}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
                user.isActive
                  ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/40'
                  : 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/40'
              }`}
            >
              {statusSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : user.isActive ? <Ban className="h-3.5 w-3.5" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
              {user.isActive ? 'Deactivate' : 'Reactivate'}
            </button>
          </div>
        </div>

        {notice && (
          <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 rounded-lg text-sm text-green-700 dark:text-green-300 mb-4">
            {notice}
          </div>
        )}
        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-lg text-sm text-red-700 dark:text-red-300 mb-4">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Personal Information</h4>
            <dl className="space-y-2.5 text-sm">
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Email</dt>
                <dd className="font-medium text-gray-900 dark:text-white">{user.email}</dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Mobile</dt>
                <dd className="font-medium text-gray-900 dark:text-white">{user.mobile}</dd>
              </div>
              {user.company?.name && (
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">Company</dt>
                  <dd className="font-medium text-gray-900 dark:text-white">{user.company.name}</dd>
                </div>
              )}
              <div>
                <dt className="text-gray-500 dark:text-gray-400 mb-1">Role</dt>
                {isEditing ? (
                  <div className="flex items-center gap-2">
                    <select value={roleDraft} onChange={(e) => setRoleDraft(e.target.value)} className={ui.input} disabled={saving}>
                      {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                    <button onClick={saveRole} disabled={saving} className="p-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white transition-colors disabled:opacity-50 shrink-0">
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                    </button>
                    <button onClick={() => setIsEditing(false)} disabled={saving} className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 transition-colors disabled:opacity-50 shrink-0">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <dd><Badge tone={ROLE_TONE[user.role] || 'neutral'}>{user.role}</Badge></dd>
                )}
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400 mb-1">Status</dt>
                <dd><Badge tone={user.isActive ? 'success' : 'danger'}>{user.isActive ? 'Active' : 'Inactive'}</Badge></dd>
              </div>
            </dl>
          </div>
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Wallet Information</h4>
            <div className="text-center py-2">
              <div className="text-2xl font-semibold text-gray-900 dark:text-white">
                {user.wallet?.balance || 0} {user.wallet?.currency || 'USD'}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Current Balance</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
